import { DragDropFile } from '../DragDropFile';
import { useEffect, useState, forwardRef, useRef, useImperativeHandle } from 'react';
import { getFileUrl } from '../../services/s3';
import { UploadFilesToS3 } from '../UploadFilesToS3';
import { query, save, deleteRecord } from '../../services/object';
import { Box, Card, CardContent, CardActions, CardMedia, Divider, Typography, Button, IconButton, Breadcrumbs, Link, MenuItem, Menu, Stack } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { config, styles } from '../../style/application';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { faFile } from '@fortawesome/free-solid-svg-icons';

import { iconMapping } from './iconMapping';

import CircularProgress from '@mui/material/CircularProgress';
import './style.css';
import DownloadForOfflineIcon from '@mui/icons-material/DownloadForOffline';
import { format } from 'date-fns';
import { styled, alpha } from '@mui/material/styles';

const StyledMenu = styled((props) => (
    <Menu
        elevation={0}
        anchorOrigin={{
            vertical: 'bottom',
            horizontal: 'right',
        }}
        transformOrigin={{
            vertical: 'top',
            horizontal: 'right',
        }}
        {...props}
    />
))(({ theme }) => ({
    '& .MuiPaper-root': {
        borderRadius: 6,
        marginTop: theme.spacing(1),
        minWidth: 180,
        color: theme.palette.mode === 'light' ? 'rgb(55, 65, 81)' : theme.palette.grey[300],
        boxShadow: 'rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px',
        '& .MuiMenu-list': {
            padding: '4px 0',
        },
        '& .MuiMenuItem-root': {
            '& .MuiSvgIcon-root': {
                fontSize: 18,
                color: theme.palette.text.secondary,
                marginRight: theme.spacing(1.5),
            },
            '&:active': {
                backgroundColor: alpha(theme.palette.primary.main, theme.palette.action.selectedOpacity),
            },
        },
    },
}));

export const DocumentCard = (props) => {
    const {
        item,
        onCardClick,
        getIcon,
        getColor,
        onDownload,
        CircularProgressWithLabel,
        onDeleteClick,
        getExtension,
        readonly,
        disabled,
        displayItem,
        displayItemSizes = {},
        absoluteMenuIcon,
    } = props;

    const [actionAnchorEl, setActionAnchorEl] = useState(null);
    const actionOpen = Boolean(actionAnchorEl);

    const handleActionMenuClick = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setActionAnchorEl(event.currentTarget);
    };

    const handleActionMenuClose = (event) => {
        event.stopPropagation();
        event.preventDefault();
        setActionAnchorEl(null);
    };

    const handleActionSelect = (event, action) => {
        event.stopPropagation();
        event.preventDefault();
        if (action === 'Delete') {
            onDeleteClick(item);
        }
        setActionAnchorEl(null);
    };

    const textEllipsisWidth = {
        xs: '180px',
        lg: '212px',
    };

    return (
        <Grid key={item.id || item.name} xs={12} md={displayItem ? displayItem : 2} lg={displayItem ? displayItem : 3} item {...displayItemSizes}>
            {!readonly && (
                <Card
                    sx={{
                        position: 'relative',
                        cursor: 'pointer',
                        background: 'white',
                        borderRadius: '8px',
                        border: '1px dashed var(--Neutral-200, #D0D5DD)',
                        boxShadow: 'none',
                    }}
                    onClick={() => onCardClick(item)}
                >
                    <CardContent>
                        {item.status === 'started' && (
                            <Box className="progress-container">
                                {' '}
                                <CircularProgressWithLabel value={item.percentage || 5}></CircularProgressWithLabel>{' '}
                            </Box>
                        )}
                        <Stack direction={'row'} justifyContent={'start'} alignItems={'start'} gap={'16px'}>
                            <Stack sx={{ color: getColor(item?.fileName) }} gap={'10px'}>
                                <FontAwesomeIcon style={{ fontSize: '3rem' }} icon={getIcon(item?.fileName)} />
                            </Stack>
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
                                <Box>
                                    <Typography title={item.name} noWrap gutterBottom variant="h6" component="div" sx={{ width: textEllipsisWidth }}>
                                        {item.name}
                                    </Typography>

                                    <Typography sx={{ mt: '-6px' }} noWrap variant="caption" color="text.secondary" component="div">
                                        {item.createdDate && format(new Date(item.createdDate), 'MMM d, yyyy HH:mm a')}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            )}
            {readonly && (
                <Card sx={{ position: 'relative', cursor: 'pointer', p: '8px 15px' }} onClick={(event) => onDownload(event, item)}>
                    <CardContent sx={{ padding: '0', paddingBottom: '0px !important' }}>
                        <Stack direction={'row'} justifyContent={'start'} alignItems={'start'} gap={'16px'}>
                            <Stack sx={{ color: getColor(item?.fileName) }} gap={'10px'}>
                                <FontAwesomeIcon style={{ fontSize: '3rem' }} icon={getIcon(item?.fileName)} />
                            </Stack>
                            <Stack direction={'row'} justifyContent={'space-between'} alignItems={'center'} sx={{ width: '100%' }}>
                                <Box>
                                    <Typography title={item.name} noWrap gutterBottom variant="h6" component="div" sx={{ width: textEllipsisWidth }}>
                                        {item.name}
                                    </Typography>
                                    <Typography sx={{ mt: '-6px' }} noWrap variant="caption" color="text.secondary" component="div">
                                        {item.createdDate && format(new Date(item.createdDate), 'MMM d, yyyy HH:mm a')}
                                    </Typography>
                                </Box>
                                {!disabled && (
                                    <Box
                                        sx={
                                            absoluteMenuIcon
                                                ? {
                                                      position: 'absolute',
                                                      right: '0',
                                                      top: '10px',
                                                  }
                                                : {}
                                        }
                                    >
                                        <IconButton
                                            aria-label="more"
                                            id="long-button"
                                            aria-controls={actionOpen ? 'long-menu' : undefined}
                                            aria-expanded={actionOpen ? 'true' : undefined}
                                            aria-haspopup="true"
                                            onClick={handleActionMenuClick}
                                        >
                                            <MoreVertIcon />
                                        </IconButton>
                                        <Menu id="long-menu" anchorEl={actionAnchorEl} open={actionOpen} onClose={(event) => handleActionMenuClose(event)} slotProps={{}}>
                                            {['Delete'].map((option) => (
                                                <MenuItem key={option} onClick={(event) => handleActionSelect(event, option)}>
                                                    {option}
                                                </MenuItem>
                                            ))}
                                        </Menu>
                                    </Box>
                                )}
                            </Stack>
                        </Stack>
                    </CardContent>
                </Card>
            )}
        </Grid>
    );
};
